import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Spinner } from '@/components/feedback'
import { useToast } from '@/hooks/useToast'
import {
  resolveLinkSlug,
  saveAttributionToStorage,
  type ReferralAttribution,
} from '@/lib/api/referralClientApi'

/**
 * Route handler for `/r/:slug`.
 *
 * Flow:
 *   1. Call GET /api/icc/referral/link/:slug — backend records the attribution
 *      and returns the destination URL.
 *   2. Persist attribution to localStorage so the checkout flow can auto-apply it.
 *   3. Navigate to the destination (falls back to `/courses`).
 *
 * The user sees a minimal loading spinner for the ~200-400ms round-trip. If the
 * slug is bad or the network fails we fall back to `/courses` so the user isn't
 * stuck on a dead page.
 */
export function ReferralLinkRedirect() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) {
      navigate('/courses', { replace: true })
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        const res = await resolveLinkSlug(slug)
        if (cancelled) return

        const attribution: ReferralAttribution = {
          code: res.code ?? null,
          slug: res.slug,
          influencerName: res.influencerName,
          studentDiscountPercent: res.studentDiscountPercent,
          expiresAt: res.attributionExpiresAt,
        }
        saveAttributionToStorage(attribution)

        const who = res.influencerName || 'a friend'
        const discount = res.studentDiscountPercent
        const message =
          discount && discount > 0
            ? `You're using ${who}'s referral! Get ${discount}% off.`
            : `You're using ${who}'s referral!`
        toast('success', message, 6000)

        const destination = res.redirectUrl && res.redirectUrl.trim().length > 0
          ? res.redirectUrl
          : '/courses'

        // External URL (starts with http) — hard-navigate; otherwise use SPA navigate.
        if (/^https?:\/\//i.test(destination)) {
          window.location.replace(destination)
        } else {
          navigate(destination, { replace: true })
        }
      } catch (e) {
        if (cancelled) return
        console.warn('Referral link resolve failed:', e)
        setError('That referral link is not valid.')
        // Still get the user somewhere useful after a moment.
        setTimeout(() => navigate('/courses', { replace: true }), 1500)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [slug, navigate, toast])

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex flex-col items-center gap-3">
        <Spinner size="lg" />
        <p className="text-[var(--color-text-secondary)] text-sm">
          {error ?? 'Redirecting...'}
        </p>
      </div>
    </div>
  )
}

export default ReferralLinkRedirect
